/**
 * authenticated-fetch
 *
 * Browser fetch helper for session-sensitive API requests.
 *
 * Primary responsibilities:
 * - Deduplicate bounded server-session preflight before protected API fanout
 * - Retry bounded auth failures with forced session refresh before surfacing
 *
 * Dependencies:
 * - lib/auth/client-session-recovery
 */

import { recoverClientSession } from '@/lib/auth/client-session-recovery'
import { hasDeliberateLogoutIntent } from '@/lib/auth/client-auth-state'
import { fetchWithNetworkRetry } from '@/lib/net/fetch-with-network-retry'

const AUTH_FAILURE_STATUSES = new Set([401, 403])
const AUTH_FAILURE_RETRY_LIMIT = 5
const AUTH_FAILURE_RETRY_BASE_DELAY_MS = 220
const SERVER_AUTH_PREFLIGHT_TTL_MS = 45_000
const AUTH_PREFLIGHT_EXCLUDED_PATHS = new Set([
  '/api/auth',
  '/api/auth/logout',
  '/api/auth/password-session',
  '/api/auth/session-sync'
])

let lastServerAuthPreflightAt = 0
let serverAuthPreflightInFlight: Promise<boolean> | null = null

export const isAuthFailureResponse = (response: Response) =>
  AUTH_FAILURE_STATUSES.has(response.status)

const createAbortError = () => {
  if (typeof DOMException === 'function') {
    return new DOMException('The operation was aborted.', 'AbortError')
  }

  return Object.assign(new Error('The operation was aborted.'), {
    name: 'AbortError'
  })
}

const throwIfAborted = (signal?: AbortSignal | null) => {
  if (signal?.aborted) {
    throw createAbortError()
  }
}

const waitForAuthRetry = (attempt: number, signal?: AbortSignal | null) =>
  new Promise<void>((resolve, reject) => {
    throwIfAborted(signal)

    const timeout = window.setTimeout(
      () => {
        signal?.removeEventListener('abort', abort)
        resolve()
      },
      Math.min(1_500, AUTH_FAILURE_RETRY_BASE_DELAY_MS * attempt)
    )

    const abort = () => {
      window.clearTimeout(timeout)
      signal?.removeEventListener('abort', abort)
      reject(createAbortError())
    }

    signal?.addEventListener('abort', abort, { once: true })
  })

const getRequestUrl = (input: RequestInfo | URL) => {
  if (typeof window === 'undefined') {
    return null
  }

  const rawUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url

  try {
    return new URL(rawUrl, window.location.origin)
  } catch {
    return null
  }
}

const shouldPreflightServerAuth = (input: RequestInfo | URL) => {
  if (typeof window === 'undefined' || hasDeliberateLogoutIntent()) {
    return false
  }

  const url = getRequestUrl(input)
  if (!url || url.origin !== window.location.origin) {
    return false
  }

  if (!url.pathname.startsWith('/api/')) {
    return false
  }

  return !AUTH_PREFLIGHT_EXCLUDED_PATHS.has(url.pathname)
}

const ensureRecentServerAuth = async () => {
  if (Date.now() - lastServerAuthPreflightAt < SERVER_AUTH_PREFLIGHT_TTL_MS) {
    return true
  }

  if (serverAuthPreflightInFlight) {
    return serverAuthPreflightInFlight
  }

  serverAuthPreflightInFlight = recoverClientSession({ forceRefresh: false })
    .then(result => {
      if (result.serverAuthorized && !hasDeliberateLogoutIntent()) {
        lastServerAuthPreflightAt = Date.now()
        return true
      }

      return false
    })
    .catch(() => false)
    .finally(() => {
      serverAuthPreflightInFlight = null
    })

  return serverAuthPreflightInFlight
}

export async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
) {
  const canRecoverBrowserSession = typeof window !== 'undefined'

  throwIfAborted(init.signal)

  if (shouldPreflightServerAuth(input)) {
    await ensureRecentServerAuth()
    throwIfAborted(init.signal)
  }

  let response = await fetchWithNetworkRetry(
    input,
    {
      ...init,
      credentials: init.credentials ?? 'include'
    },
    { context: { source: 'authenticated-fetch' } }
  )

  if (!isAuthFailureResponse(response)) {
    return response
  }

  if (!canRecoverBrowserSession || hasDeliberateLogoutIntent()) {
    return response
  }

  for (let attempt = 1; attempt <= AUTH_FAILURE_RETRY_LIMIT; attempt += 1) {
    lastServerAuthPreflightAt = 0
    const recovery = await recoverClientSession({ forceRefresh: true })
    throwIfAborted(init.signal)

    if (!recovery.serverAuthorized || hasDeliberateLogoutIntent()) {
      if (attempt === AUTH_FAILURE_RETRY_LIMIT) {
        return response
      }

      await waitForAuthRetry(attempt, init.signal)
      continue
    }

    lastServerAuthPreflightAt = Date.now()

    response = await fetchWithNetworkRetry(
      input,
      {
        ...init,
        credentials: init.credentials ?? 'include'
      },
      { context: { source: 'authenticated-fetch' } }
    )

    if (!isAuthFailureResponse(response)) {
      return response
    }

    if (attempt < AUTH_FAILURE_RETRY_LIMIT) {
      await waitForAuthRetry(attempt, init.signal)
    }
  }

  return response
}
